import { css } from '@emotion/react'
import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { VList, VListHandle } from 'virtua'
import { loadMessageHistory } from '../../../../../actions/message'
import { PMSTATE } from '../../../../../state/state.pm'
import { hexRGBA, lightenNormalized } from '../../../../../utilities/colors'
import {
    AppStateType,
    useAppStateSelector,
} from '../../../../Hooks/useAppState'
import { useLayoutSelector } from '../../../../Hooks/useLayout'
import { usePMChatSelector } from '../../../../Hooks/usePMChat'
import { useRangeChange } from '../../../../Hooks/useRangeChange'
import { useUserSelector } from '../../../../Hooks/useUser'
import { palette } from '../../../../styles/colors'
import PMStatus from '../PMStatus'
import PMChatRow from './PMChatRow'
import PMChatTypingIndicator from './PMChatTypingIndicator'

const resolveColor = (color, isDarkMode) => {
    // if (fullWindow) {
    //     return '#FFFFFF'
    // }

    if (isDarkMode && color) {
        return lightenNormalized(color)
    }

    return color || '#999999'
}

const fullWindowMyColors = {
    boxShadow: `1px 1px 1px 0px ${palette.chatPmBubbleBackgroundMeFullWindow}`,
    backgroundColor: palette.chatPmBubbleBackgroundMeFullWindow,
    color: '#FFFFFF',
}
const myColors = {
    boxShadow: `1px 1px 1px 0px ${hexRGBA('#388e3c', 0.2)}`,
    backgroundColor: hexRGBA('#388e3c', 0.1),
    color: palette.chatPmBubbleText,
}

const fullWindowTheirColors = {
    boxShadow: `1px 1px 1px 0px ${palette.chatPmBubbleBackgroundFullWindow}`,
    backgroundColor: palette.chatPmBubbleBackgroundFullWindow,
    marker: '#FFFFFF',
    color: '#FFFFFF',
}

const fullWindowChatStyles = css({
    width: '100%',
    WebkitMaskImage:
        'linear-gradient(to top, black 0%, 95%, rgba(0,0,0,0) 100%)',
    maskImage: 'linear-gradient(to top, black 0%, 95%, rgba(0,0,0,0) 100%)',
})
const chatStyles = css({
    width: '100%',
    WebkitMaskImage: 'none',
    maskImage: 'none',
})

const getTheirColors = (color, isDarkMode) => {
    const resolvedColor = resolveColor(color, isDarkMode)
    return {
        boxShadow: `1px 1px 1px 0px ${hexRGBA(resolvedColor, 0.2)}`,
        backgroundColor: hexRGBA(resolvedColor, 0.1),
        marker: '#999999',
        color: resolvedColor,
    }
}

export const PMChat = memo((props: any) => {
    const { styleDisplay, styleHeight, height, chatRef, id, isQuickReply } =
        props

    const isDarkMode = useAppStateSelector(AppStateType.APP, 'darkMode')
    const isExhausted = usePMChatSelector(id, 'isExhausted')
    const isTyping = useUserSelector(id, 'isTypingPM')
    const fullWindow = useLayoutSelector('fullWindow')
    const history = usePMChatSelector(id, 'history')
    const latest = usePMChatSelector(id, 'latest')
    const color = useUserSelector(id, 'color')
    const isInitializing = useRef(true)
    const shouldStickToBottom = useRef(true)
    const [queue, setQueue] = useState([])
    const [rows, setRows] = useState([])
    const containerRef = useRef(null)
    const ref = useRef<VListHandle>(null)
    const loadMoreTimeout = useRef(null)
    const componentID = useRef(0)
    const shift = useRef(false)
    const previousLatest = useRef(latest)
    const isFullWindow = fullWindow && !isQuickReply

    // const myColors = useMemo(() => {
    //     const boxShadowColor = fullWindow
    //         ? palette.chatPmBubbleBackgroundMeFullWindow
    //         : hexRGBA('#388e3c', 0.2)
    //     return {
    //         boxShadow: `1px 1px 1px 0px ${boxShadowColor}`,
    //         backgroundColor: fullWindow
    //             ? palette.chatPmBubbleBackgroundMeFullWindow
    //             : hexRGBA('#388e3c', 0.1),
    //         color: fullWindow ? '#FFFFFF' : palette.chatPmBubbleText,
    //     }
    // }, [fullWindow])

    // const theirColors = useMemo(() => {
    //     const resolvedColor = resolveColor(color, isDarkMode)
    //     const boxShadowColor = fullWindow
    //         ? palette.chatPmBubbleBackgroundFullWindow
    //         : hexRGBA(resolvedColor, 0.2)
    //     return {
    //         boxShadow: `1px 1px 1px 0px ${boxShadowColor}`,
    //         backgroundColor: fullWindow
    //             ? palette.chatPmBubbleBackgroundFullWindow
    //             : hexRGBA(resolvedColor, 0.1),
    //         marker: fullWindow ? '#FFFFFF' : '#999999',
    //         color: resolvedColor,
    //     }
    // }, [fullWindow, isDarkMode, color])

    const getRows = useCallback(
        (config) => {
            const { isExhausted, isTyping, history, latest } = config
            const messages = [...history, ...latest]
            const theirColors = getTheirColors(color, isDarkMode)
            const rows = [
                <PMStatus
                    key={`loading_${++componentID.current}`}
                    isEmpty={messages.length === 0}
                    EmptyAction={'send a message'}
                    isExhausted={isExhausted}
                />,
                ...messages.map((message) => {
                    return (
                        <PMChatRow
                            isFullWindow={isFullWindow}
                            theirColors={
                                isFullWindow
                                    ? fullWindowTheirColors
                                    : theirColors
                            }
                            myColors={
                                isFullWindow ? fullWindowMyColors : myColors
                            }
                            message={message}
                            key={message.id}
                            id={id}
                        />
                    )
                }),
            ]

            if (isTyping) {
                return [
                    ...rows,
                    <PMChatTypingIndicator
                        key={`typing_${++componentID.current}`}
                        backgroundColor={isFullWindow ? '#888888' : color}
                        show
                    />,
                ]
            }

            return rows
        },
        [id, isFullWindow, color],
    )

    useEffect(() => {
        if (!containerRef.current || !chatRef) {
            return
        }

        chatRef.current = containerRef.current
    }, [containerRef.current, chatRef])

    useEffect(() => {
        return () => {
            clearTimeout(loadMoreTimeout.current)
        }
    }, [])

    useEffect(() => {
        if (!history.length && !isExhausted) {
            return
        }

        const config = {
            latest:
                previousLatest.current !== latest
                    ? previousLatest.current
                    : latest,
            isExhausted,
            history,
        }

        setQueue((p) => [...p, { prepend: true, rows: getRows(config) }])
    }, [isExhausted, history])

    useEffect(() => {
        previousLatest.current = latest

        const config = {
            isExhausted,
            isTyping,
            history,
            latest,
        }

        setQueue((p) => [...p, { prepend: false, rows: getRows(config) }])
    }, [isTyping, latest, getRows])

    useEffect(() => {
        if (!queue.length) {
            return
        }

        const event = queue.shift()

        shift.current = isInitializing.current ? false : event.prepend

        setRows(event.rows)
        setQueue([...queue])
    }, [queue])

    useEffect(() => {
        if (!ref.current || !shouldStickToBottom.current) {
            return
        }

        ref.current.scrollToIndex(rows.length - 1, {
            align: 'end',
        })

        const timeout = setTimeout(() => {
            /*
                Marc: Once in a while, the scroll position will not go all
                the way to the bottom for larger messages, and I speculate that
                it's due to this hook (some times) running before the virtual
                list has accounted for the height of the message or images that
                have yet to load. So I'm putting this in place as a reassurance.
            */

            if (!ref.current) {
                return
            }

            ref.current.scrollToIndex(rows.length - 1, {
                align: 'end',
            })
        }, 75)

        return () => {
            clearTimeout(timeout)
        }
    }, [rows])

    const onLoadMore = useCallback(() => {
        const { isExhausted, isLoading } = PMSTATE.chats[id]

        if (isLoading || isExhausted) {
            return
        }

        clearTimeout(loadMoreTimeout.current)

        loadMoreTimeout.current = setTimeout(() => {
            loadMessageHistory(id)
        }, 1000)
    }, [id])

    const onRangeChange = useCallback(
        (start: number, end: number) => {
            if (!start && !end) {
                onLoadMore()
                return
            }

            if (start === end || (isInitializing.current && start === 0)) {
                return
            }

            isInitializing.current = false
            shift.current = false

            shouldStickToBottom.current = end === rows.length - 1

            if (start === 0) {
                shift.current = true
                onLoadMore()
            }
        },
        [rows, isTyping, onLoadMore],
    )

    const onScroll = useRangeChange(ref, onRangeChange, 200)

    const onContainerRef = useCallback(
        (node) => {
            if (!containerRef) {
                return
            }

            containerRef.current = node
        },
        [containerRef],
    )

    if (rows.length === 0) {
        return null
    }

    return (
        <div
            style={{
                height: styleHeight || '100%',
                display: styleDisplay,
                width: '100%',
            }}
            ref={onContainerRef}
        >
            {isFullWindow && (
                <div
                    style={{
                        height: height < 600 ? '25%' : '33%',
                        width: '100%',
                    }}
                />
            )}
            <VList
                className="no-scrollbars"
                css={isFullWindow ? fullWindowChatStyles : chatStyles}
                style={{
                    height: isFullWindow
                        ? height < 600
                            ? '75%'
                            : '67%'
                        : '100%',
                }}
                data-testid="Chat__Pane"
                onScroll={onScroll}
                shift={shift.current}
                cache={null}
                ref={ref}
                reverse
            >
                {rows}
            </VList>
        </div>
    )
})

export default PMChat
